import React, { FC, useState, useMemo, useCallback, useEffect } from 'react';
import { useIntl, useLocation, history } from 'umi';
import { Button, DatePicker } from 'tea-component';
import AutoSaveModal from '@sg/components/AutoSaveModal';
import RoutePrompt from '@sg/components/RoutePrompt';
import DatePickerWrapper from '@gp/components/DatePickerWrapper';
import GameSelectAll from '@sg/components/GameSelectAll';
import LiveFinanceOverview from './component/LiveFinanceOverview';
import { useLiveFinance } from './hooks/useLiveFinance';
import { useLiveFinanceAutoSave } from './hooks/useLiveFinanceAutoSave';
import {
  buildLiveTitlesUrl,
  parseLiveTitlesUrl,
} from '@sg/pages/StudioManagement/LiveTitles/utils/liveTitlesUrl';
import { createBtnPoint, SGBuriedBtnPoint, LiveTitlesTab } from '@/pages/strategic/utils/buriedPoint';
import { getButtonPermission } from '@sg/_config/permission';
import SvgIcon from '@home/components/SvgIcon';
import MetricsDownloadModal, {
  LIVE_FINANCE_DOWNLOAD_CONFIG,
} from '@sg/pages/StudioManagement/component/MetricsDownloadModal';
import './index.scss';

const LiveFinancePage: FC = () => {
  const location = useLocation();
  const { gameTitle } = useMemo(
    () => parseLiveTitlesUrl(location.search),
    [location.search],
  );
  const [isEditing, setIsEditing] = useState(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const { formatMessage } = useIntl();
  const { MonthPicker } = DatePicker;
  const canEditFinance = useMemo(
    () => getButtonPermission('studio_liveTitles_notes'),
    [],
  );

  // Finance v1 has no per-game routes yet; canonicalize URL to ALL until game list API ships.
  useEffect(() => {
    if (!gameTitle || gameTitle === 'all') {
      return;
    }
    history.replace(
      buildLiveTitlesUrl({ liveView: 'finance', gameTitle: 'all' }),
    );
  }, [gameTitle]);

  const fetchFinanceGameData = async () => {
    // TODO: replace with finance game list API when backend is ready.
    return [];
  };

  const handleFinanceGameChange = (game: any) => {
    // Track buried point for finance game selection (separate from business tab).
    const gameName = game?.name || 'Unknown';
    const gameId = game?.unified_id || '';
    const btnPoint = createBtnPoint(
      LiveTitlesTab,
      'studioSelect',
      `Finance Game Select - ${gameName}${gameId ? ` (ID: ${gameId})` : ''}`,
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
    // URL update is handled by GameSelectAll via onBuildUrl; page content syncs from URL later.
  };

  const handleFinanceBuildUrl = useCallback(
    (_loc: any, unifiedId: string) =>
      buildLiveTitlesUrl({ liveView: 'finance', gameTitle: unifiedId }),
    [],
  );

  const urlUpdateCondition = useCallback((loc: any) => {
    const params = new URLSearchParams(loc.search);
    return params.get('subtitle') === 'titles';
  }, []);

  const {
    selectedMonth,
    setSelectedMonth,
    disabledMonth,
    summary,
    studioCover,
    studioName,
    projects,
    projectOrderList,
    setProjects,
    setProjectOrderList,
    overviewLoading,
    loading,
    handleProjectsReorder,
    handleProjectsReorderCancel,
    handleConfirm,
    captureEditBaseline,
  } = useLiveFinance();

  const {
    autoSaveVisible,
    hasUnsavedChanges,
    captureBaseline,
    handleEditWithAutoSave,
    handleAutoSaveConfirm,
    handleAutoSaveCancel,
    clearAutoSave,
  } = useLiveFinanceAutoSave({
    isEditing,
    selectedMonth,
    projects,
    projectOrderList,
    setProjects,
    setProjectOrderList,
  });

  const enterEditMode = useCallback(() => {
    if (!canEditFinance) {
      return;
    }
    captureBaseline(projects || [], projectOrderList || []);
    captureEditBaseline();
    setIsEditing(true);
  }, [
    canEditFinance,
    captureBaseline,
    captureEditBaseline,
    projects,
    projectOrderList,
  ]);

  const handleEditToggle = () => {
    handleEditWithAutoSave(() => {
      const btnPoint = createBtnPoint(
        LiveTitlesTab,
        'liveFinanceEdit',
        'Edit LiveFinance',
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
      enterEditMode();
    });
  };

  const handleSave = async () => {
    const saved = await handleConfirm();
    if (saved) {
      clearAutoSave();
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    handleProjectsReorderCancel();
    clearAutoSave();
    setIsEditing(false);
  };

  // Finance v1: overall (ALL) content only; per-game view when API provides titles.
  const showAllView = !gameTitle || gameTitle === 'all';

  return (
    <>
      <GameSelectAll
        fetchData={fetchFinanceGameData}
        onChange={handleFinanceGameChange}
        defaultUnifiedId={gameTitle}
        urlParamKey="game_title"
        urlBasePath="/strategic/home"
        onBuildUrl={handleFinanceBuildUrl}
        urlUpdateCondition={urlUpdateCondition}
      />
      {showAllView && (
        <div className="dbs-live-finance dbs-home-studio-card-studio">
          <div className="dbs-studio-filter">
            <div className="dbs-segment-row">
              <div className="dbs-segment-row__left">
                <DatePickerWrapper
                  className="dbs-month-picker-with-tooltip"
                  label={formatMessage({ id: 'dbs_month' })}
                >
                  <MonthPicker
                    overlayClassName="dbs-month-picker-overlay"
                    value={selectedMonth}
                    onChange={setSelectedMonth}
                    disabledMonth={disabledMonth}
                    placeholder="Please Select"
                    disabled={isEditing}
                  />
                </DatePickerWrapper>
              </div>
              <div className="dbs-segment-row__right">
                {canEditFinance && !isEditing && (
                  <Button
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleEditToggle();
                    }}
                    className="dbs-gradient-btn"
                    disabled={loading}
                  >
                    {formatMessage({ id: 'dbs_edit' })}
                  </Button>
                )}
                {canEditFinance && isEditing && (
                  <div className="dbs-rich-text-buttons">
                    <Button
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleSave();
                      }}
                      className="dbs-gradient-btn"
                      disabled={loading}
                    >
                      {formatMessage({ id: 'dbs_confirm' })}
                    </Button>
                    <Button
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleCancel();
                      }}
                      style={{ height: '36px', minWidth: '80px', marginLeft: '10px' }}
                      className="dbs-outline-btn"
                      disabled={loading}
                    >
                      {formatMessage({ id: 'dbs_cancel' })}
                    </Button>
                  </div>
                )}
                <div
                  className={`dbs-ltf-download-btn${loading ? ' is-disabled' : ''}`}
                  onClick={(e) => {
                    if (loading) return;
                    e?.stopPropagation();
                    const btnPoint = createBtnPoint(
                      LiveTitlesTab,
                      'liveFinanceDownload',
                      'Download LiveFinance',
                    );
                    if (btnPoint) {
                      SGBuriedBtnPoint(btnPoint);
                    }
                    setDownloadModalVisible(true);
                  }}
                >
                  <SvgIcon type="download" />
                </div>
              </div>
            </div>
          </div>

          <LiveFinanceOverview
            summary={summary}
            studioCover={studioCover}
            studioName={studioName}
            projects={projects}
            overviewLoading={overviewLoading}
            isEditing={canEditFinance && isEditing}
            handleProjectsReorder={handleProjectsReorder}
          />
        </div>
      )}

      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={() => {
          const savedData = handleAutoSaveConfirm();
          // Use pre-edit baseline so handleConfirm still detects reordered changes vs server state.
          captureEditBaseline(
            savedData?.baselineProjects,
            savedData?.baselineProjectOrderList,
          );
          setIsEditing(true);
        }}
        onClose={() => {
          handleAutoSaveCancel();
          enterEditMode();
        }}
      />

      <RoutePrompt
        when={hasUnsavedChanges}
        onOk={async () => {
          await handleSave();
        }}
      />

      <MetricsDownloadModal
        visible={downloadModalVisible}
        config={LIVE_FINANCE_DOWNLOAD_CONFIG}
        activeDateTypeSegment="month"
        selectedMonth={selectedMonth}
        onClose={() => setDownloadModalVisible(false)}
        caption="Financial Data Download"
        defaultFileFormat="csv"
        modalSize={500}
      />
    </>
  );
};

export default LiveFinancePage;
